import { useEffect, useState } from 'react'
import type { BookingSettings, SizeChartRow } from '@fitting-room/contracts'
import { PageHeader } from '../../components/admin/PageHeader'
import { useSaveSettings, useSettings } from '../../lib/adminQueries'
import { useStaffToken } from '../../lib/staffToken'
import { BackOfficeSection } from './settings/BackOfficeSection'
import { TeamSection } from './settings/TeamSection'

interface SizeRowForm {
  size: string
  fits: string
}

/** The size chart as the form edits it: one row per street size, its samples as typed. */
function toSizeRows(settings: BookingSettings): SizeRowForm[] {
  return settings.sizeChart.map((row) => ({ size: row.size, fits: row.fits.join(', ') }))
}

const list = (raw: string) =>
  raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)

function toChart(rows: readonly SizeRowForm[]): SizeChartRow[] {
  return rows.filter((row) => row.size.trim() !== '').map((row) => ({ size: row.size.trim(), fits: list(row.fits) }))
}

/**
 * The owner's settings: who is on the team, the back office's vocabulary and
 * numbers, and which sample sizes each street size is shown.
 *
 * When customers may book is not here. They book in Koalendar, which owns the
 * calendar; the fitting room follows that booking rather than offering one of
 * its own, so the hours it used to edit here were a second answer to a
 * question Koalendar already answers. Saving the size chart leaves the
 * appointment types' stored hours exactly as they are.
 *
 * Three saves rather than one, because they are three different kinds of
 * edit: the team saves each change as it is made (a list of people is fixed
 * one person at a time), the back-office settings are one document with one
 * Save, and the size chart keeps the Save it always had.
 */
export function SettingsPage() {
  const { token } = useStaffToken()
  const query = useSettings(token)
  const save = useSaveSettings(token)
  const [sizes, setSizes] = useState<SizeRowForm[]>([])

  useEffect(() => {
    if (query.data) setSizes(toSizeRows(query.data))
  }, [query.data])

  const submit = () => save.mutate({ sizeChart: toChart(sizes) })

  if (query.isPending) return <p className="st-meta">Loading…</p>
  if (query.isError) return <p className="st-alert">Could not load the settings. Check the access token and try again.</p>

  return (
    <div>
      <PageHeader eyebrow="The owner's" title="Settings" />

      <TeamSection />

      <BackOfficeSection tiers={query.data.tiers} />

      <section>
        <h2 className="st-display mb-1 text-xl">Size chart</h2>
        <p className="st-meta mb-4 max-w-2xl">
          The sizes a customer picks from, and which off-the-rack sample sizes each one is shown. Leave the samples
          blank to show that size everything.
        </p>
        <table className="st-table max-w-2xl">
          <thead>
            <tr>
              <th>Customer&rsquo;s size</th>
              <th>Sample sizes they can try</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sizes.map((row, index) => (
              <tr key={index}>
                <td className="w-32">
                  <input
                    className="st-input w-24"
                    aria-label={`Size ${index + 1}`}
                    value={row.size}
                    onChange={(event) =>
                      setSizes((current) => current.map((r, i) => (i === index ? { ...r, size: event.target.value } : r)))
                    }
                  />
                </td>
                <td>
                  <input
                    className="st-input w-full"
                    aria-label={`Sample sizes for size ${row.size || index + 1}`}
                    placeholder="8, 10, 12"
                    value={row.fits}
                    onChange={(event) =>
                      setSizes((current) => current.map((r, i) => (i === index ? { ...r, fits: event.target.value } : r)))
                    }
                  />
                </td>
                <td className="w-20">
                  <button
                    type="button"
                    className="st-btn-quiet"
                    onClick={() => setSizes((current) => current.filter((_, i) => i !== index))}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className="st-btn-ghost mt-3" onClick={() => setSizes((current) => [...current, { size: '', fits: '' }])}>
          Add a size
        </button>
      </section>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button type="button" className="st-btn" disabled={save.isPending} onClick={submit}>
          {save.isPending ? 'Saving…' : 'Save size chart'}
        </button>
        {save.isError ? (
          <span className="st-alert">
            That didn&rsquo;t save: {save.error.message} <span className="text-xs opacity-70">({save.error.code})</span>
          </span>
        ) : null}
        {save.isSuccess ? <span className="st-ok">Saved. The fitting room is using these now.</span> : null}
      </div>
    </div>
  )
}
